import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { collaborationAPI } from '../../services/api';
import Button from '../common/Button';
import toast from 'react-hot-toast';
import RequestDetailModal from '../collaboration/RequestDetailModal';
import Avatar from '../common/Avatar';
import ConfirmationModal from '../common/ConfirmationModal';

interface CollaborationRequest {
  id: string;
  sender: {
    id: string;
    first_name: string;
    last_name: string;
    profile_pic: string;
    profile_pic_signed: string;
    user_type: string;
  };
  receiver: {
    id: string;
    first_name: string;
last_name: string;
    profile_pic: string;
    profile_pic_signed: string;
  };
  type: string;
  subject: string;
  message: string;
  status: string;
  urgency: string;
  created_at: string;
  expires_at: string;
}

interface CollaborationRequestsWidgetProps {
  onlySent?: boolean;
}

const CollaborationRequestsWidget: React.FC<CollaborationRequestsWidgetProps> = ({ onlySent = false }) => {
  const [requests, setRequests] = useState<CollaborationRequest[]>([]);
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>(onlySent ? 'sent' : 'received');
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [requestToWithdraw, setRequestToWithdraw] = useState<string | null>(null);

  useEffect(() => {
    fetchCollaborationRequests();
  }, [activeTab]);

  const fetchCollaborationRequests = async () => {
    try {
      setLoading(true);
      const response = await collaborationAPI.getCollaborationRequests({
        type: activeTab,
        collab_type: 'all',
        limit: 10
      });
      const data = response.data.data.data;
      const requestsList = activeTab === 'received' ? data.receivedRequests : data.sentRequests;
      const filteredRequests = (requestsList || []).filter((request: CollaborationRequest) => {return request.status != 'expired'}).slice(0, 3);
      setRequests(filteredRequests);
    } catch (error) {
      console.error('Failed to fetch collaboration requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (requestId: string, status: 'accepted' | 'declined') => {
    try {
      await collaborationAPI.respondToCollaborationRequest(requestId, {
        status,
        response_message: status === 'accepted' 
          ? "I'd love to collaborate with you!" 
          : "Thanks for reaching out, but I'm not available at the moment."
      });
      
      toast.success(`Request ${status} successfully!`);
      fetchCollaborationRequests();
    } catch (error) {
      console.error('Failed to respond to request:', error);
      toast.error('Failed to respond to request');
    }
  };

  const handleWithdraw = (requestId: string) => {
    setRequestToWithdraw(requestId);
    setShowWithdrawModal(true);
  };

  const confirmWithdraw = async () => {
    if (!requestToWithdraw) return;

    try {
      await collaborationAPI.withdrawCollaborationRequest(requestToWithdraw);
      toast.success('Request withdrawn successfully');
      fetchCollaborationRequests();
      setSelectedRequest(null);
      setShowWithdrawModal(false);
      setRequestToWithdraw(null);
    } catch (error) {
      console.error('Failed to withdraw request:', error);
      toast.error('Failed to withdraw request');
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiration = (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiration <= 24 && hoursUntilExpiration > 0;
  };

  const formatShortTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return formatDistanceToNow(date, { addSuffix: true });
  };

  return (
    <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-gray-200 dark:bg-gray-900 dark:border-gray-800">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">Collaboration Requests</h2>
        <Link 
          to="/collaboration-requests"
          className="text-sm text-teal-600 hover:text-teal-700 font-medium dark:text-teal-400 dark:hover:text-teal-300"
        >
          View All →
        </Link>
      </div>

      {/* Tab Navigation - Hide if onlySent is true */}
      {!onlySent && (
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-4 dark:bg-gray-800">
          <button
            onClick={() => setActiveTab('received')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'received'
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            Received
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'sent'
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            Sent
          </button>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {loading ? (
          // ... (loading skeleton) ...
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg dark:bg-gray-800">
                  <div className="w-10 h-10 bg-gray-200 rounded-full dark:bg-gray-700"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4 dark:bg-gray-700"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 dark:bg-gray-700"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : requests.length > 0 ? (
          requests.map((request, index) => ( request.status != 'expired' &&
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={e => setSelectedRequest(request)}
              className="p-3 md:p-4 bg-white border border-gray-100 rounded-xl hover:shadow-md transition-all duration-200 cursor-pointer group dark:bg-gray-800 dark:border-gray-700 dark:hover:border-teal-900"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Avatar
                    src={activeTab === 'received' ? request.sender.profile_pic_signed : request.receiver.profile_pic_signed}
                    alt={activeTab === 'received' 
                      ? `${request.sender.first_name} ${request.sender.last_name}`
                      : `${request.receiver.first_name} ${request.receiver.last_name}`
                    }
                    size="md"
                    className="ring-2 ring-gray-50 dark:ring-gray-700"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900 text-xs md:text-sm dark:text-white">
                      {activeTab === 'received' 
                        ? `${request.sender.first_name} ${request.sender.last_name}`
                        : `${request.receiver.first_name} ${request.receiver.last_name}`
                      }
                    </h3>
                    <p className="text-[10px] md:text-xs text-gray-500 capitalize dark:text-gray-400">
                      {activeTab === 'received' ? request.sender.user_type : 'Request sent'}
                    </p>
                  </div>
                </div>

                {/* Status & Urgency Badges */}
                <div className="flex items-center space-x-1">
                  {request.urgency !== 'normal' && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${getUrgencyColor(request.urgency)}`}>
                      {request.urgency === 'urgent' && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
                      {request.urgency}
                    </span>
                  )}
                  
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                    request.status === 'pending' 
                      ? 'bg-red-50 text-red-700 border border-red-100 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
                      : request.status === 'accepted'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                        : 'bg-rose-50 text-rose-700 border border-rose-100 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800'
                  }`}>
                    {request.status}
                  </span>
                </div>
              </div>

              <div className="mb-3 pl-[52px]">
                <h4 className="font-semibold text-gray-900 mb-1 text-xs md:text-sm dark:text-white">{request.subject}</h4>
                <p className="text-[10px] md:text-xs text-gray-500 line-clamp-2 leading-relaxed dark:text-gray-400">{request.message}</p>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-400 pl-[52px] dark:text-gray-500">
                <div className="flex items-center space-x-2">
                  <span className="capitalize font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full dark:bg-teal-900/30 dark:text-teal-400">
                    {request.type}
                  </span>
                  <span>•</span>
                  <span>{formatShortTime(request.created_at)}</span>
                </div>

                {isExpiringSoon(request.expires_at) && request.status === 'pending' && (
                  <span className="text-orange-600 font-medium flex items-center dark:text-orange-400">
                    <ClockIcon className="h-3 w-3 mr-1" />
                    Expiring soon
                  </span>
                )}
              </div>

              {/* Action Buttons for Received Requests */}
              {activeTab === 'received' && request.status === 'pending' && (
                <div className="flex space-x-2 mt-4 pl-[52px]">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e?.stopPropagation(); handleResponse(request.id, 'declined'); }}
                    className="flex-1 !py-1.5 !text-xs border-gray-200 text-gray-600 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-rose-900/20 dark:hover:text-rose-400"
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={(e) => { e?.stopPropagation(); handleResponse(request.id, 'accepted'); }}
                    className="flex-1 !py-1.5 !text-xs bg-teal-600 hover:bg-teal-700 border-transparent dark:bg-teal-500 dark:hover:bg-teal-600"
                  >
                    Accept
                  </Button>
                </div>
              )}
            </motion.div>
          ))
        ) : (
          <div className="text-center py-8">
            <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-3 dark:text-gray-600" />
            <p className="text-gray-500 text-sm dark:text-gray-400">
              {activeTab === 'received' 
                ? 'No collaboration requests received yet'
                : 'No collaboration requests sent yet'
              }
            </p>
            <p className="text-gray-400 text-xs mt-1 dark:text-gray-500">
              {activeTab === 'received' 
                ? 'When others send you collaboration requests, they\'ll appear here'
                : 'Visit user profiles to send collaboration requests'
              }
            </p>
          </div>
        )}
      </div>
      {/* Request Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onResponse={handleResponse}
          onWithdraw={handleWithdraw}
          isReceived={activeTab === 'received'}
        />
      )}

      {/* Withdraw Confirmation Modal */}
      <ConfirmationModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        onConfirm={confirmWithdraw}
        title="Withdraw Request"
        message="Are you sure you want to withdraw this collaboration request? This action cannot be undone."
        confirmText="Withdraw"
        type="danger"
      />
    </div>
  );
};

export default CollaborationRequestsWidget;
